import type { AdminChatMedia, AdminMediaFile, AdminMediaVideo, MediationTimelineEvent, MediationTimelineType } from "@expertloop/shared";

/** Un élément du fil reconstitué : ce que voient les deux parties, événements compris. */
export type ConversationItem =
	| { kind: "request"; ref: "request"; at: string; content: unknown; chatId: string }
	| { kind: "message"; ref: string; at: string; sender: string; messageId: string; content: unknown; files: AdminMediaFile[]; videos: AdminMediaVideo[] }
	| {
			kind: "delivery";
			ref: string;
			at: string;
			deliveryId: string;
			content?: unknown;
			rejectionReason?: string;
			files: AdminMediaFile[];
			/** Pièces de l'aperçu offert au client avant l'acceptation. */
			previewFiles: AdminMediaFile[];
			videos: AdminMediaVideo[];
			/** Montant que l'expert annonce avec cette livraison, en unités mineures. */
			amount?: number;
	  }
	| { kind: "event"; ref: string; at: string; event: MediationTimelineEvent };

export interface ConversationSource {
	chat: { id: string; requestMessage?: unknown; createdAt: string };
	messages: Array<{ id: string; sender: string; content: unknown; attachments?: string[]; createdAt: string }>;
	deliveries: Array<{
		id: string;
		message?: unknown;
		rejectionReason?: string;
		createdAt: string;
	}>;
	/** Pièces de la conversation (fichiers, vocaux, livraisons, vidéos), sans lien. */
	media: AdminChatMedia;
	timeline: MediationTimelineEvent[];
	/** Montant annoncé par livraison, lu dans l'historique du prix. */
	deliveryAmounts: Record<string, number>;
}

/**
 * Fil unique de la médiation, dans l'ordre exact des faits : demande initiale, messages,
 * livraisons (avec leurs pièces et leur montant) et événements (consultation, acceptation, paiement,
 * remboursement, médiation). Les messages système n'y figurent pas, comme chez les parties.
 */
export function buildConversation(source: ConversationSource): ConversationItem[] {
	const items: ConversationItem[] = [];
	if (source.chat.requestMessage) {
		items.push({ kind: "request", ref: "request", at: source.chat.createdAt, content: source.chat.requestMessage, chatId: source.chat.id });
	}
	for (const message of source.messages) {
		if (message.sender === "system") continue;
		items.push({
			kind: "message",
			ref: `message:${message.id}`,
			at: message.createdAt,
			sender: message.sender,
			messageId: message.id,
			content: message.content,
			files: source.media.files.filter((file) => file.messageId === message.id),
			videos: source.media.videos.filter((video) => video.messageId === message.id),
		});
	}
	const deliveryIds = new Set(source.deliveries.map((delivery) => delivery.id));
	for (const delivery of source.deliveries) {
		items.push({
			kind: "delivery",
			ref: `delivery:${delivery.id}`,
			at: delivery.createdAt,
			deliveryId: delivery.id,
			content: delivery.message,
			...(delivery.rejectionReason ? { rejectionReason: delivery.rejectionReason } : {}),
			files: source.media.files.filter((file) => file.deliveryId === delivery.id && file.kind === "delivery"),
			previewFiles: source.media.files.filter((file) => file.deliveryId === delivery.id && file.kind === "delivery_preview"),
			videos: source.media.videos.filter((video) => video.deliveryId === delivery.id),
			...(source.deliveryAmounts[delivery.id] !== undefined ? { amount: source.deliveryAmounts[delivery.id] } : {}),
		});
	}
	for (const event of source.timeline) {
		if (event.kind === "message") continue;
		// La publication d'une livraison est portée par sa carte
		if (event.type === "delivery_published" && event.targetId && deliveryIds.has(event.targetId)) continue;
		items.push({ kind: "event", ref: event.ref, at: String(event.at), event });
	}
	const rank = (item: ConversationItem) => (item.kind === "request" ? 0 : 1);
	return items.sort((a, b) => rank(a) - rank(b) || new Date(a.at).getTime() - new Date(b.at).getTime());
}

/** Élément du fil que désigne une référence du rapport (la commande renvoie à l'autorisation du paiement). */
export function conversationRef(ref: string): string {
	return ref === "order" ? "event:payment-authorized" : ref;
}

export function conversationElementId(ref: string): string {
	return `conv-${conversationRef(ref)}`;
}

export type EventTone = "success" | "warning" | "error" | "muted" | "neutral";

/**
 * Couleur et icône d'un événement, reprises des jalons du fil des parties (vert pour le paiement
 * et l'acceptation, ocre pour la médiation et les refus de livrable, rouge pour un refus
 * d'annulation, gris pour une simple trace), pour que le support lise les mêmes teintes.
 */
export const EVENT_STYLE: Record<MediationTimelineType, { tone: EventTone; icon: string }> = {
	message: { tone: "neutral", icon: "fa-comment" },
	delivery_published: { tone: "neutral", icon: "fa-box" },
	delivery_withdrawn: { tone: "muted", icon: "fa-ban" },
	delivery_viewed: { tone: "muted", icon: "fa-eye" },
	delivery_accepted: { tone: "success", icon: "fa-check" },
	delivery_rejected: { tone: "warning", icon: "fa-times-circle" },
	delivery_auto_accepted: { tone: "success", icon: "fa-clock" },
	delivery_complement_published: { tone: "neutral", icon: "fa-box-open" },
	delivery_complement_acknowledged: { tone: "muted", icon: "fa-hand" },
	delivery_appreciated: { tone: "muted", icon: "fa-face-smile" },
	payment_authorized: { tone: "success", icon: "fa-credit-card" },
	payment_captured: { tone: "success", icon: "fa-check-double" },
	cancellation_requested: { tone: "warning", icon: "fa-exclamation-triangle" },
	cancellation_approved: { tone: "success", icon: "fa-check-circle" },
	cancellation_denied: { tone: "error", icon: "fa-times-circle" },
	refund_requested: { tone: "warning", icon: "fa-rotate-left" },
	refund_executed: { tone: "success", icon: "fa-undo" },
	mediation_opened: { tone: "warning", icon: "fa-gavel" },
	mediation_decided: { tone: "warning", icon: "fa-gavel" },
	mediation_reopened: { tone: "warning", icon: "fa-gavel" },
};
